import { t } from "@lingui/core/macro";
import { Grid, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { Outlet } from "react-router";
import AppLayout from "@/components/AppLayout";
import ErrorPage from "@/components/ErrorPage";
import { useCurrency } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { usePriceCheck } from "@/hooks/usePriceCheck";
import { usePrices } from "@/hooks/usePrices";
import BookingLayoutSkeleton from "./BookingLayoutSkeleton";
import BookingProgressIndicator from "./BookingProgressIndicator";
import BookingSummaryCard from "./BookingSummaryCard";
import CancellationTerms from "./CancellationTerms";
import PaymentInfo from "./PaymentInfo";

export default function BookingLayout() {
  const { currencyCode } = useCurrency();
  const { state, actions } = useBookingContext();

  const { checkPrice } = usePriceCheck();
  const { fetchPrices } = usePrices();
  const [loadError, setLoadError] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: should update when currency code changes
  useEffect(() => {
    fetchPrices().catch(() => setLoadError(true));
  }, [currencyCode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: updated when currency or extras change
  useEffect(() => {
    const { dateFrom, dateUntil, selectedCampsite, selectedPersons, selectedExtras } = state;
    if (!dateFrom || !dateUntil || !selectedCampsite || Object.keys(selectedPersons).length === 0)
      return;
    actions.setPriceLoading(true, null);
    checkPrice({
      dateFrom,
      dateUntil,
      campsiteId: selectedCampsite.id,
      persons: selectedPersons,
      extras: selectedExtras,
    })
      .then((result) => {
        actions.setPrice(result);
        actions.setPriceLoading(false, null);
      })
      .catch(() => {
        actions.setPriceLoading(false, null);
        if (!state.price) setLoadError(true);
      });
  }, [JSON.stringify(state.selectedExtras), currencyCode]);

  if (loadError) {
    return <ErrorPage message={t`Could not load booking prices. Please go back and try again.`} />;
  }

  if (!state.price || !state.prices) {
    return <BookingLayoutSkeleton />;
  }

  return (
    <AppLayout title={<BookingProgressIndicator />}>
      <Grid container spacing={3} p={{ xs: 0, sm: 2 }}>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ display: { xs: "none", sm: "block" } }}>
          <Stack spacing={2.5} position="sticky" top={115}>
            <BookingSummaryCard />
            <CancellationTerms />
            <PaymentInfo />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <Outlet />
        </Grid>
      </Grid>
    </AppLayout>
  );
}
